// vg-binding: P7.D-01
// vg-binding: P7.D-02
// vg-binding: P7.D-04
// vg-binding: P7.D-05
// vg-binding: P7.D-07
// vg-binding: P7.D-99
// vg-binding: P7.D-146
//
// apps/vendor/src/pages/Dashboard.tsx
//
// Phase 7 Wave 4 Task 12 — vendor dashboard combined layout.
//
// Layout (matches design/vendor_dashboard PNG):
//   ┌──────────────────────────────────────────────┐
//   │ WelcomeBanner (greeting + 1-line stats)      │
//   ├────────────────────┬─────────────────────────┤
//   │ KpiCardGrid (2x3)  │ CapacityMeter           │
//   ├────────────────────┴─────────────────────────┤
//   │ Quick actions (4 buttons)                    │
//   ├────────────────────┬─────────────────────────┤
//   │ MiniProductionQueue│ RecentEarnings          │
//   └────────────────────┴─────────────────────────┘
//
// Polling: 30s via TanStack-Query `refetchInterval` (set inside the
// hooks). KPI + queue widgets each handle their own loading skeleton.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@printway/ui';

import { useDashboardKpis, useProductionQueue } from '../features/dashboard/api.js';
import { CapacityMeter } from '../features/dashboard/CapacityMeter.js';
import { GettingStartedChecklist } from '../features/dashboard/GettingStartedChecklist.js';
import { KpiCardGrid } from '../features/dashboard/KpiCardGrid.js';
import { MiniProductionQueue } from '../features/dashboard/MiniProductionQueue.js';
import { RecentEarnings } from '../features/dashboard/RecentEarnings.js';
import { useOnboardingStatus } from '../features/onboarding/api.js';
import { AnnouncementBanner } from '../features/announcements/AnnouncementBanner.js';

// ---------------------------------------------------------------------------
// Quick action bar — amendment (removed "Accept All" — orders
// are auto-routed in).
// ---------------------------------------------------------------------------

interface QuickActionProps {
  testId: string;
  label: string;
  to: string;
  icon: JSX.Element;
}

function QuickAction({ testId, label, to, icon }: QuickActionProps): JSX.Element {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-1 items-center gap-3 rounded-xl border border-pw-border bg-pw-bg px-4 py-3 shadow-sm transition-colors hover:bg-pw-surface"
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-md bg-pw-primary/10 text-pw-primary"
      >
        {icon}
      </span>
      <span className="text-sm font-medium text-pw-text">{label}</span>
    </Link>
  );
}

const I = {
  play: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  ),
  urgent: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  flag: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  ),
};

export function Dashboard(): JSX.Element {
  const { t } = useTranslation('dashboard');
  const user = useAuth((s) => s.user);
  const kpisQ = useDashboardKpis();
  const queueQ = useProductionQueue(5);
  const statusQ = useOnboardingStatus();
  const kycApproved = statusQ.data?.kyc_status === 'approved';

  return (
    <main
      data-testid="dashboard-root"
      className="flex flex-col gap-6 px-6 py-6"
    >
      {/* Welcome banner — orange gradient (retained) */}
      <div className="rounded-xl bg-gradient-to-r from-pw-primary to-orange-400 p-6 text-white shadow-sm">
        <h1 className="text-2xl font-semibold">
          {t('page.welcomeTitle', { name: user?.displayName || t('page.defaultName') })}
        </h1>
        <p className="mt-1 text-sm opacity-95">
          {t('page.welcomeSubtitle')}
        </p>
        {/* UAT #2027 — bằng chứng auto-refresh 30s: timestamp nhảy mỗi lần
            TanStack refetch, kể cả khi số liệu không đổi. */}
        {kpisQ.dataUpdatedAt ? (
          <p
            data-testid="dashboard-last-updated"
            className="mt-2 text-xs opacity-80"
          >
            {t('page.lastUpdated', {
              time: new Date(kpisQ.dataUpdatedAt).toLocaleTimeString(),
            })}
            {kpisQ.isFetching ? ' ⟳' : ''}
          </p>
        ) : null}
      </div>

      {/* Admin system announcement targeted at surface=page:dashboard. */}
      <AnnouncementBanner surface="page:dashboard" />

      {/* Quick actions */}
      <section className="flex flex-col gap-3 md:flex-row">
        <QuickAction
          testId="dashboard-quick-action-start-production-all"
          label={t('quickActions.startProductionAll')}
          to="/orders?filter=ready_to_start"
          icon={I.play}
        />
        <QuickAction
          testId="dashboard-quick-action-scan-order"
          label={t('quickActions.scanOrder')}
          to="/orders/scan"
          icon={I.scan}
        />
        <QuickAction
          testId="dashboard-quick-action-view-urgent-orders"
          label={t('quickActions.urgentOrders')}
          to="/orders?filter=urgent"
          icon={I.urgent}
        />
        <QuickAction
          testId="dashboard-quick-action-report-issue"
          label={t('quickActions.reportIssue')}
          to="/support"
          icon={I.flag}
        />
      </section>

      {/* KPI cards + capacity meter */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <KpiCardGrid
          metrics={kpisQ.data?.metrics ?? []}
          isLoading={kpisQ.isLoading}
        />
        <CapacityMeter data={kpisQ.data?.capacity} isLoading={kpisQ.isLoading} />
      </section>

      {/* Getting started — only visible until vendor dismisses or finishes */}
      <GettingStartedChecklist enabled={kycApproved} />

      {/* Production queue + recent earnings */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <MiniProductionQueue
          // Hide BE-seeded placeholder rows (Sample product · placeholder-order-N)
          // when no real orders exist — show empty state instead.
          rows={
            queueQ.data?.data_source === 'placeholder'
              ? []
              : (queueQ.data?.rows ?? [])
          }
          isLoading={queueQ.isLoading}
        />
        <RecentEarnings />
      </section>
    </main>
  );
}

export default Dashboard;
